import config from "../Configurations";
import { collectClientFingerprint } from "./clientFingerprint";
import { getAnonymousSessionId, trackAnonymousEvent } from "./anonymousAnalytics";

const TRUST_STORAGE_KEY = "pivony_auth_challenge_trusted_until";

/** Login-only captcha bypass for corporate domains blocked by shared-IP reCAPTCHA. */
export const LOGIN_CAPTCHA_EXEMPT_EMAIL_DOMAINS = Object.freeze(["etstur.com"]);

export const normalizeAuthEmailDomain = (email) => {
  const normalized = String(email || "").trim().toLowerCase();
  const at = normalized.lastIndexOf("@");
  if (at < 0) {
    return "";
  }
  return normalized.slice(at + 1);
};

export const isLoginCaptchaExemptEmail = (email) => {
  const domain = normalizeAuthEmailDomain(email);
  return LOGIN_CAPTCHA_EXEMPT_EMAIL_DOMAINS.includes(domain);
};

export const isAuthCaptchaExemptOrigin = () => {
  if (typeof document === "undefined") return false;
  const origin = document.location.origin;
  return (
    origin === "http://localhost:3000" ||
    origin === "https://demo.pivony.com"
  );
};

export const isRiskBasedCaptchaEnabled = () =>
  Boolean(config.RECAPTCHA_ENABLED && config.RISK_BASED_CAPTCHA);

const challengeUrl = (path) => `${config.BASE_URL}organization_engagement/anonymous/${path}`;

const readLocalTrust = () => {
  try {
    const raw = sessionStorage.getItem(TRUST_STORAGE_KEY);
    if (!raw) return false;
    const until = Date.parse(raw);
    if (Number.isFinite(until) && until > Date.now()) return true;
    sessionStorage.removeItem(TRUST_STORAGE_KEY);
  } catch {
    // ignore
  }
  return false;
};

const writeLocalTrust = (trustedUntil) => {
  if (!trustedUntil) return;
  try {
    sessionStorage.setItem(TRUST_STORAGE_KEY, String(trustedUntil));
  } catch {
    // ignore
  }
};

export const buildPreAuthCheckPayload = (payload = {}) => ({
  ...payload,
  session_id: getAnonymousSessionId(),
  client_fingerprint: collectClientFingerprint(),
});

export const fetchAuthChallengeStatus = async () => {
  if (!isRiskBasedCaptchaEnabled() || isAuthCaptchaExemptOrigin()) {
    return { challenge_required: false, trusted: true, reasons: [] };
  }
  if (readLocalTrust()) {
    return { challenge_required: false, trusted: true, reasons: ["local_trust"] };
  }

  const body = {
    session_id: getAnonymousSessionId(),
    client_fingerprint: collectClientFingerprint(),
    page_path: typeof window !== "undefined" ? window.location.pathname : null,
  };

  try {
    const resp = await fetch(challengeUrl("challenge-status"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await resp.json();
    if (resp.status === 403 && data?.blocked) {
      trackAnonymousEvent("bot_blocked", {
        event_category: "auth",
        properties: {
          block_reason: data.block_reason,
          bot_score: data.bot_score,
          bot_signals: data.bot_signals || [],
          hard_signals: data.hard_signals || [],
          code: data.code,
          ip_ban: data.ip_ban,
        },
      });
      return {
        blocked: true,
        challenge_required: false,
        trusted: false,
        reasons: data.reasons || ["bot_blocked"],
        ...data,
      };
    }
    if (!resp.ok || !data?.success) {
      return { challenge_required: false, trusted: false, reasons: ["status_error"] };
    }
    if (data.challenge_required) {
      trackAnonymousEvent("challenge_required", {
        event_category: "auth",
        properties: {
          reasons: data.reasons || [],
          bot_score: data.bot_score,
          bot_signals: data.bot_signals || [],
        },
      });
    }
    return data;
  } catch {
    return { challenge_required: false, trusted: false, reasons: ["network_error"] };
  }
};

export const verifyAuthChallengeToken = async (token, meta = {}) => {
  if (!token) return { verified: false };

  const body = {
    recaptcha_token: token,
    session_id: getAnonymousSessionId(),
    client_fingerprint: collectClientFingerprint(),
    page_path: typeof window !== "undefined" ? window.location.pathname : null,
    reasons: meta.reasons || [],
    bot_score: meta.bot_score,
  };

  try {
    const resp = await fetch(challengeUrl("challenge-verify"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await resp.json();
    if (resp.status === 403 && data?.blocked) {
      trackAnonymousEvent("bot_blocked", {
        event_category: "auth",
        properties: {
          block_reason: data.block_reason,
          bot_score: data.bot_score,
          reasons: meta.reasons || [],
        },
      });
      return { verified: false, blocked: true, ...data };
    }
    if (resp.ok && data?.verified) {
      writeLocalTrust(data.trusted_until);
      trackAnonymousEvent("challenge_passed", {
        event_category: "auth",
        properties: { reasons: meta.reasons || [] },
      });
      return { verified: true, ...data };
    }
    trackAnonymousEvent("challenge_failed", {
      event_category: "auth",
      properties: {
        error: data?.error || "verify_failed",
        reasons: meta.reasons || [],
      },
    });
    return { verified: false, ...data };
  } catch {
    trackAnonymousEvent("challenge_failed", {
      event_category: "auth",
      properties: { error: "network_error", reasons: meta.reasons || [] },
    });
    return { verified: false, error: "network_error" };
  }
};

export const trackChallengeShown = (status = {}) => {
  trackAnonymousEvent("challenge_shown", {
    event_category: "auth",
    properties: {
      reasons: status.reasons || [],
      bot_score: status.bot_score,
      bot_signals: status.bot_signals || [],
    },
  });
};

export const shouldShowAuthCaptcha = ({
  challengeRequired = false,
  failedAuth = false,
  email = "",
  context = "login",
}) => {
  if (!config.RECAPTCHA_ENABLED || isAuthCaptchaExemptOrigin()) {
    return false;
  }
  if (context === "login" && isLoginCaptchaExemptEmail(email)) {
    return false;
  }
  // Always show the checkbox on login/signup when reCAPTCHA is enabled.
  // Risk-based mode still drives server-side token verification.
  return true;
};

export const isAuthCaptchaSatisfied = ({
  challengeRequired = false,
  checkRecaptcha = false,
  email = "",
  context = "login",
}) => {
  if (!config.RECAPTCHA_ENABLED || isAuthCaptchaExemptOrigin()) {
    return true;
  }
  if (context === "login" && isLoginCaptchaExemptEmail(email)) {
    return true;
  }
  return Boolean(checkRecaptcha);
};
